import React from 'react';
import {
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    BarChart,
    Bar,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Treemap
} from 'recharts';
import { Globe, PieChart as PieChartIcon, Zap } from 'lucide-react';
import { ChartCard, MetricCard, EmptyState, COLORS, formatRevenue } from './common.jsx';

const CompetitiveTab = ({ data }) => {
    const {
        genreCompetition = [],
        topPublishers = [],
        publisherDominance = [],
        marketOpportunities = [],
        publisherLandscape = [],
        marketConcentration = {}
    } = data || {};

    const getSaturationColor = (level) => {
        if (!level) return 'bg-gray-100 text-gray-700';
        const lower = String(level).toLowerCase();
        if (lower.includes('very high') || lower.includes('high')) return 'bg-red-100 text-red-700';
        if (lower.includes('medium')) return 'bg-yellow-100 text-yellow-700';
        return 'bg-green-100 text-green-700';
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricCard
                    icon={<Globe className="h-8 w-8" />}
                    title="Publisher Classes"
                    value={publisherLandscape.length || 0}
                    subtitle="Distribution by class"
                    color="blue"
                />
                <MetricCard
                    icon={<PieChartIcon className="h-8 w-8" />}
                    title="Competition Intensity"
                    value={genreCompetition.length}
                    subtitle="Genres analyzed"
                    color="purple"
                />
                <MetricCard
                    icon={<Zap className="h-8 w-8" />}
                    title="Opportunities"
                    value={marketConcentration.opportunityGenres || 0}
                    subtitle="Underserved niches"
                    color="orange"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ChartCard title="Genre Competition" subtitle="Competition intensity and saturation by genre">
                    {genreCompetition && genreCompetition.length > 0 ? (
                        <ResponsiveContainer width="100%" height={400}>
                            <RadarChart
                                cx="50%"
                                cy="50%"
                                outerRadius="80%"
                                data={genreCompetition.slice(0, 8).map(g => ({
                                    ...g,
                                    saturationScore: g.saturationScore ?? g.saturation?.score ?? 0
                                }))}
                            >
                                <PolarGrid />
                                <PolarAngleAxis dataKey="genre" />
                                <PolarRadiusAxis domain={[0, 10]} />
                                <Radar name="Saturation" dataKey="saturationScore" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.6} />
                                <Tooltip formatter={(value, name) => {
                                    if (name === 'saturationScore') return value;
                                    return value.toLocaleString();
                                }} />
                            </RadarChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState message="No competitive data available" />
                    )}
                </ChartCard>

                <ChartCard title="Publisher Market Share" subtitle="Revenue distribution by publisher class">
                    {publisherLandscape && publisherLandscape.length > 0 ? (
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                                data={publisherLandscape}
                                barCategoryGap="18%"
                                margin={{ top: 12, right: 24, left: 16, bottom: 24 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="class" />
                                <YAxis tickFormatter={(value) => formatRevenue(value)} />
                                <Tooltip formatter={(value, name) => {
                                    if (name === 'totalRevenue' || name === 'avgRevenue') return formatRevenue(value);
                                    if (name === 'marketShare') return `${value.toFixed(2)}%`;
                                    return value;
                                }} />
                                <Legend />
                                <Bar dataKey="totalRevenue" fill={COLORS.primary} name="Total Revenue" barSize={16} />
                                <Bar dataKey="avgRevenue" fill={COLORS.success} name="Avg Revenue/Game" barSize={16} />
                                <Bar dataKey="marketShare" fill={COLORS.indigo} name="Market Share (%)" barSize={16} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyState message="No publisher market share data" />
                    )}
                </ChartCard>
            </div>

            <ChartCard title="Market Gaps" subtitle="Where competition is low and revenue is high" fullWidth>
                {(() => {
                    const gaps = (marketOpportunities && marketOpportunities.length > 0
                        ? marketOpportunities
                        : genreCompetition
                            .filter(g => (g.opportunity?.score ?? 0) > 0 && (g.saturation?.score ?? 0) <= 5)
                            .map(g => ({
                                name: g.name || g.genre || 'Other',
                                totalRevenue: g.totalRevenue || 1,
                                gameCount: g.gameCount,
                                saturationScore: g.saturation?.score ?? 0
                            }))
                    ).slice(0, 12);

                    if (gaps.length === 0) {
                        return <EmptyState message="No competitive opportunities identified" />;
                    }

                    return (
                        <ResponsiveContainer width="100%" height={320}>
                            <Treemap
                                data={gaps.map(opp => ({
                                    name: opp.genre || opp.name || 'Other',
                                    size: opp.totalRevenue || 1,
                                    gameCount: opp.gameCount,
                                    saturation: opp.saturationScore
                                }))}
                                dataKey="size"
                                aspectRatio={4 / 3}
                                stroke="#fff"
                                fill={COLORS.primary}
                            >
                                <Tooltip formatter={(value, name) => {
                                    if (name === 'size') return formatRevenue(value);
                                    return value;
                                }} />
                            </Treemap>
                        </ResponsiveContainer>
                    );
                })()}
            </ChartCard>

            <ChartCard title="🎮 Genre Competition Analysis" subtitle="Saturation levels and market opportunity" fullWidth>
                {genreCompetition && genreCompetition.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                        {genreCompetition.slice(0, 12).map((genre) => (
                            <div key={genre.name} className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                                <div className="flex justify-between items-start mb-3">
                                    <h4 className="font-bold text-gray-900">{genre.name || 'Unknown'}</h4>
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getSaturationColor(genre.saturation?.level)}`}>
                                        {genre.saturation?.level || 'n/a'}
                                    </span>
                                </div>

                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Games:</span>
                                        <span className="font-semibold">{genre.gameCount != null ? genre.gameCount.toLocaleString() : '—'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Revenue:</span>
                                        <span className="font-semibold">{genre.totalRevenue != null ? formatRevenue(genre.totalRevenue) : '—'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Avg Score:</span>
                                        <span className="font-semibold">{genre.avgScore != null ? genre.avgScore : '—'}/100</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Recent:</span>
                                        <span className="font-semibold">{genre.recentReleases != null ? genre.recentReleases : '—'} releases</span>
                                    </div>
                                </div>

                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs text-gray-600">Opportunity</span>
                                        <span className="text-xs font-bold text-blue-600">{Math.round(genre.opportunity?.score || 0)}/100</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-blue-600 h-2 rounded-full transition-all"
                                            style={{ width: `${genre.opportunity?.score || 0}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <EmptyState message="No genre competition data available" />
                )}
            </ChartCard>
        </div>
    );
};

export default CompetitiveTab;
